// src/components/BlogPosts.tsx
import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  Flex,
  Image,
  IconButton
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { getPosts } from "./WordpressApi";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { FONT_FAMILY, THEME_COLOR } from "../../Constant";
import { UseInView } from "../../Utils";

type Post = {
  ID: number;
  title: string ;
  excerpt: string;
  URL: string;
  post_thumbnail: {
    guid:string
  };
  attachments:{[key: string]:{
    URL:string
  }}
};

const POSTS_PER_PAGE = 9;

export const Blog = () => {
    const { isInView, elementRef } = UseInView();
    const [posts, setPosts] = useState<Post[]>([]);
    const [pages, setPages] = useState(1)
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const fetchData = async () => {
        const res = await getPosts();
        setPosts(res.data ?? []);
        setPages(res.totalPages ?? 1)
        setLoading(false);
        };
        fetchData();
    }, []);

    const totalPages = Math.ceil(pages / POSTS_PER_PAGE);

    const paginatedPosts = posts?.slice(
        (currentPage - 1) * POSTS_PER_PAGE,
        currentPage * POSTS_PER_PAGE
    );

    const handlePrev = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const cardHeight = 480
    const headHeight = 70
    const imageHeight = 250
    const contentHeight = cardHeight - imageHeight - headHeight - 80

    return  (
        loading ? (
            <Flex justify="center" alignItems={"center"}  height={"100vh"}>
            <Spinner size="xl" />
            </Flex>
        ) : (
            <Box ref={elementRef} px={{ base: 5, md: 32 }} py={{ base: 10, md: 20 }}>
                <SimpleGrid 
                    columns={{ base: 1, sm: 2, md: 3, lg: 3 }} 
                    spacing={6} 
                    className={isInView ? "animate__animated animate__zoomIn" : ""}
                >
                    {paginatedPosts.map((post) => (
                        <Box
                            key={post.ID}
                            p={5}
                            shadow="md"
                            borderWidth="1px"
                            borderRadius="lg"
                            height={`${cardHeight}px`}
                        >
                            <Flex height={`${imageHeight}px`} alignItems={"center"} justifyContent={"center"}>
                                <Image src={
                                    post?.post_thumbnail?.guid ?? 
                                    post?.attachments?.[Object.keys(post?.attachments)?.[0] as keyof typeof String]?.URL ??
                                    "/images/logo.jpg"
                                    } alt={post.title} height={"100%"}/>
                            </Flex>
                            <Flex p={"10px"} height={`${headHeight}px`} alignItems={"center"}>
                                <Heading
                                    fontSize="lg"
                                    mb={2}
                                    fontFamily={FONT_FAMILY}
                                    noOfLines={2}
                                >
                                    {post.title}
                                </Heading>
                            </Flex>
                            <Box px="5px" height={`${contentHeight}px`}>
                                <Text
                                    fontSize="sm"
                                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                                    noOfLines={3}
                                    fontFamily={FONT_FAMILY}
                                />
                            </Box>
                            <Flex alignItems={"center"} justifyContent={"center"}>
                                <Button 
                                    as="a"
                                    href={post.URL}
                                    target="_blank"  
                                    display="inline-block"
                                    letterSpacing={"1px"}
                                    gap={"5px"}
                                    pt={"10px"}
                                    bg={"transparent"}
                                    border={`2px solid ${THEME_COLOR}`}
                                    borderRadius={"50px 50px"}
                                    color={THEME_COLOR}
                                    _hover={{ background: THEME_COLOR, color: "white" }}
                                    w="8rem"
                                    fontFamily={FONT_FAMILY}
                                    fontSize={"14px"}
                                    textAlign={"center"}
                                >
                                    Read More
                                </Button>
                            </Flex>
                            
                        </Box>
                    ))}
                </SimpleGrid>

                {/* Pagination Controls */}
                {paginatedPosts?.length > 0 && (
                    <Flex justify="center" mt={6} alignItems={"center"}>
                        <IconButton
                            aria-label="Previous"
                            icon={<ArrowBackIcon />}
                            onClick={handlePrev}
                            isDisabled={currentPage === 1}
                            borderRadius="full"
                            bg="transparent"
                            border={`2px solid ${THEME_COLOR}`}
                            color={`${THEME_COLOR}`}
                            _hover={{ bg: "white", color: "black" }}
                        />
                        <Text
                            ml={2}
                            mr={2}
                            fontFamily={FONT_FAMILY}
                        >
                            {`${currentPage} / ${totalPages}`}
                        </Text>
                        <IconButton
                            aria-label="Next"
                            icon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            isDisabled={currentPage === totalPages}
                            borderRadius="full"
                            bg="transparent"
                            border={`2px solid ${THEME_COLOR}`}
                            color={`${THEME_COLOR}`}
                            _hover={{ bg: "white", color: "black" }}
                            fontFamily={FONT_FAMILY}
                        />
                    </Flex>
                )}
            </Box>
            )
    )
   
};

export default Blog;
